<template>
	<div data-tour-scope="setting-status-order">
		<v-main>
			<v-container fluid class="so-page">
				<div class="so-top mb-4">
					<div class="so-page-title">{{ $vuetify.lang.t('$vuetify.front.subsetting.statusOrder') }}</div>
					<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()"><v-icon left>mdi-school-outline</v-icon>Tutorial guidato</v-btn>
					<v-spacer></v-spacer>
				</div>

				<SectionCard title="Colori degli stati" icon="mdi-palette-outline" class="mb-4">
					<div class="so-hint mb-3">Gli stati sono fissi: qui si sceglie il colore con cui compaiono nella lista ordini. Clicca uno stato per cambiarne il colore, poi salva.</div>
					<div v-if="render" class="so-grid" data-tour="table">
						<button v-for="item of items" :key="item._id" type="button" class="so-tile" :title="'Cambia il colore di ' + nome(item)" @click="openColorPicker(item)">
							<span class="so-chip" :style="stileChip(item.color)">{{ nome(item) }}</span>
							<span class="so-tile-foot">
								<span class="so-swatch" :style="{ backgroundColor: item.color }"></span>
								<span class="so-hex">{{ item.color }}</span>
								<v-icon small class="ml-auto so-edit">mdi-pencil-outline</v-icon>
							</span>
						</button>
						<div v-if="!items.length" class="so-hint pa-3">Nessuno stato</div>
					</div>
					<div class="so-actions mt-4">
						<span v-if="modificati" class="so-dirty"><v-icon small color="orange" class="mr-1">mdi-circle-medium</v-icon>Modifiche non salvate</span>
						<v-spacer></v-spacer>
						<v-btn depressed color="primary" data-tour="save" @click="save"><v-icon left small>mdi-content-save-outline</v-icon>Salva configurazione</v-btn>
					</div>
				</SectionCard>

				<SectionCard title="Limiti per la board ordini" icon="mdi-timer-alert-outline" class="mb-4">
					<div class="so-hint mb-3">
						Per gli stati in lavorazione puoi fissare dopo quante ore un ordine fermo è in ritardo nella Board ordini. Vuoto = si usa il tempo tipico misurato (in ritardo oltre il doppio).
					</div>
					<div v-if="render" class="so-limits" data-tour="limits">
						<v-text-field
							v-for="item of itemsLavorazione"
							:key="'max' + item._id"
							v-model.number="item.max_hours"
							:label="nome(item)"
							type="number"
							min="1"
							suffix="ore"
							placeholder="automatico"
							persistent-placeholder
							outlined
							dense
							hide-details="auto"
							:rules="[regolaOre]"
						/>
					</div>
					<div class="so-actions mt-4">
						<v-spacer></v-spacer>
						<v-btn depressed color="primary" @click="save"><v-icon left small>mdi-content-save-outline</v-icon>Salva configurazione</v-btn>
					</div>
				</SectionCard>

				<order-tags />

				<v-snackbar v-model="snackbar.snackbar" :color="snackbar.color" :multi-line="snackbar.mode === 'multi-line'" :timeout="snackbar.timeout" :vertical="snackbar.mode === 'vertical'">
					{{ snackbar.text }}
					<v-btn dark text @click="snackbar.snackbar = false"> Chiudi </v-btn>
				</v-snackbar>
			</v-container>

			<ColoreDialog v-model="dialogColorPicker" :iniziale="color_to_edit.color" :etichetta="color_to_edit.nome" :titolo="'Colore di «' + color_to_edit.nome + '»'" @conferma="saveColorPicker" />
		</v-main>
		<TourOverlay :tour="tour"></TourOverlay>
	</div>
</template>

<script>
import axios from 'axios';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import { useTour } from '@/components/core/tour/useTour';
import { createStatusOrderTourSteps } from './status-order-tour';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import OrderTags from '../../components/order_tags/Index';
import SectionCard from '@/components/core/SectionCard.vue';
import ColoreDialog from './components/ColoreDialog.vue';
import { isDarkColor } from '../../Helper';
import { WORKING_STATUS_IDS } from '@/components/orderboard/format';

export default {
	name: 'StatusOrder',
	setup() {
		return { tour: useTour(createStatusOrderTourSteps()) };
	},
	// un solo beforeDestroy: prima ce n'erano due e il secondo annullava il primo (il tutorial non si fermava)
	beforeDestroy() {
		this.tour.stop();
		this.destroyData();
	},
	components: { OrderTags, TourOverlay, SectionCard, ColoreDialog },
	data() {
		return {
			items: [],
			lang: '',
			languages: [],
			snackbar: {
				snackbar: false,
				color: '',
				mode: '',
				timeout: 6000,
				text: '',
			},
			color_to_edit: {
				_id: '',
				color: '',
				nome: '',
			},
			dialogColorPicker: false,
			render: false,
			// colori come all'apertura o all'ultimo salvataggio, per segnalare le modifiche in sospeso
			salvati: {},
		};
	},

	async created() {
		this.setPreload(true);
		try {
			await this.fetchSettings();
			const statuses = await this.fetchStatusOrders({});
			this.lang = _.find(this.getSettings.languages, { default: true }).lang;
			this.getSettings.languages = _.orderBy(this.getSettings.languages, ['default'], ['desc']);
			this.languages = _.filter(this.getSettings.languages, { is_active: true });

			// il colore entra prima di assegnare items: aggiunto dopo non sarebbe reattivo
			this.items = _.orderBy(statuses.data, ['order'], ['asc']).map(item => {
				const check = _.find(this.getSettings.status_order, { _id: item._id });
				return { ...item, color: check && check.color ? check.color : '#FFFFFFFF', max_hours: check && check.max_hours > 0 ? check.max_hours : null };
			});
			this.ricordaSalvati();
			this.render = true;
		} finally {
			this.setPreload(false);
		}
	},

	computed: {
		modificati() {
			return this.items.some(item => this.salvati[item._id] !== this.firma(item));
		},
		itemsLavorazione() {
			return this.items.filter(item => WORKING_STATUS_IDS.includes(item._id));
		},
		...mapGetters({
			getToken: 'profile/getToken',
			getUrl: 'settings/getUrl',
			getSettings: 'settings/getSetting',
		}),
	},

	methods: {
		...mapMutations({
			setLogout: 'profile/setLogout',
			setPreload: 'settings/setPreload',
		}),
		...mapActions({
			fetchSettings: 'settings/asyncFetchData',
			destroyData: 'settings/destroyData',
			setSettings: 'settings/updateData',
			fetchStatusOrders: 'statusorders/fetchLists',
		}),
		nome(item) {
			const t = _.find(item.trans, { lang: this.lang }) || (item.trans || [])[0];
			return t ? t.value : '';
		},
		// stesso stile del chip nella lista ordini
		stileChip(colore) {
			return { backgroundColor: colore, color: isDarkColor(colore) ? '#FFFFFF' : '#000000' };
		},
		firma(item) {
			return `${item.color}|${item.max_hours || ''}`;
		},
		ricordaSalvati() {
			this.salvati = _.fromPairs(this.items.map(item => [item._id, this.firma(item)]));
		},
		regolaOre(value) {
			return value === null || value === '' || value === undefined || Number(value) > 0 || 'Ore maggiori di zero, oppure vuoto';
		},
		openColorPicker(item) {
			this.color_to_edit = { _id: item._id, color: item.color, nome: this.nome(item) };
			this.dialogColorPicker = true;
		},
		saveColorPicker(colore) {
			const check = _.find(this.items, { _id: this.color_to_edit._id });
			if (check) check.color = colore;
		},
		mostra(colore, testo) {
			this.snackbar.color = colore;
			this.snackbar.text = testo;
			this.snackbar.snackbar = true;
			this.snackbar.mode = 'multi-line';
			this.snackbar.timeout = 5000;
		},
		save() {
			this.setPreload(true);
			axios
				// limite vuoto o non valido = automatico
				.post(`${this.getUrl}profile/status-order`, this.items.map(item => ({ ...item, max_hours: Number(item.max_hours) > 0 ? Number(item.max_hours) : null })), { headers: { Authorization: `Bearer ${this.getToken}` } })
				.then(({ data }) => {
					this.setSettings(data.data);
					this.ricordaSalvati();
					this.mostra('success', 'Operazione effettuata con successo');
				})
				.catch(err => {
					if (err.response) {
						const errors = err.response.data.error;
						if (errors && !_.isArray(errors)) err.message = errors;
						if (err.response.status === 401) {
							this.setLogout();
							this.$router.push('/login');
						}
					}
					this.mostra('error', err.message);
				})
				.finally(() => {
					this.setPreload(false);
				});
		},
	},
};
</script>

<style scoped>
.so-top {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
}
.so-page-title {
	font-size: 20px;
	font-weight: 500;
	color: #1f2d3d;
}
.so-limits {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	gap: 12px;
}
.so-hint {
	font-size: 12px;
	color: #5f6b7a;
}
.so-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
	gap: 10px;
}
.so-tile {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 10px;
	padding: 12px;
	text-align: left;
	background: #fff;
	border: 1px solid #e1e7ef;
	border-radius: 10px;
	cursor: pointer;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.so-tile:hover,
.so-tile:focus-visible {
	border-color: #1565c0;
	box-shadow: 0 2px 8px rgba(21, 101, 192, 0.12);
	outline: none;
}
.so-tile:hover .so-edit {
	color: #1565c0;
}
.so-chip {
	display: inline-flex;
	align-items: center;
	height: 26px;
	max-width: 100%;
	padding: 0 12px;
	border-radius: 13px;
	border: 1px solid rgba(0, 0, 0, 0.12);
	font-size: 13px;
	font-weight: 500;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.so-tile-foot {
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	font-size: 12px;
	color: #5f6b7a;
}
.so-swatch {
	width: 16px;
	height: 16px;
	border-radius: 4px;
	border: 1px solid #c4c4c4;
}
.so-hex {
	font-family: 'Roboto Mono', ui-monospace, monospace;
}
.so-actions {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
}
.so-dirty {
	display: inline-flex;
	align-items: center;
	font-size: 13px;
	color: #b26a00;
}
</style>
